// Scheduler.js
import i18n from "../i18n";
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import moment from 'moment';
import config from '../config';
import Schedules from './Schedules';
import { useBpFlags } from '../Responsive';

const tr = (...args) => i18n.t(...args);

const Scheduler = () => {

    const { isTablet } = useBpFlags();
    const viewportRef = useRef(null);
    const contentRef  = useRef(null);
    const [scale, setScale] = useState(1);
    const [selectedWeek, setSelectedWeek] = useState(moment().startOf('isoWeek'));
    const [areas, setAreas] = useState([]);
    const [schedules, setSchedules] = useState([]);
    const [users, setUsers] = useState([]);
    const [categories, setCategories] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [selectedUsers, setSelectedUsers] = useState({});

    useEffect(() => {
        fetchUsers();
        fetchAreas();
    }, []);

    useEffect(() => {
        fetchCategories();
    }, [areas]);

    useEffect(() => {
        fetchSchedules();
    }, [users, categories, selectedWeek, showModal]);

    // Auto-fit scale on tablet
    useEffect(() => {
      if (!isTablet) { setScale(1); return; }
      const compute = () => {
        const vp = viewportRef.current;
        const el = contentRef.current;
        if (!vp || !el) return;
        const vw = vp.clientWidth - 16;
        const cw = el.scrollWidth;
        const s = Math.max(0.75, Math.min(1, vw / Math.max(1, cw)));
        setScale(s);
      };
      compute();
      window.addEventListener('resize', compute);
      return () => window.removeEventListener('resize', compute);
    }, [isTablet, schedules, categories, users, selectedWeek, showModal]);

    useEffect(() => {
        fetchUsersSchedules();
    }, [schedules]);

    const fetchAreas = async () => {
        try {
            const response = await axios.get(tr('{{API_BASE_URL}}/areas', {
                API_BASE_URL: config.API_BASE_URL
            }));
            setAreas(response.data);
        } catch (error) {
            console.error('Error fetching areas:', error);
        }
    };

    const fetchSchedules = async () => {
        try {
            const response = await axios.get(tr('{{API_BASE_URL}}/schedules', {
                API_BASE_URL: config.API_BASE_URL
            }));
            setSchedules(response.data);
        } catch (error) {
            console.error('Error fetching schedules:', error);
        }
    };

    const fetchUsersSchedules = async () => {
        const weekNumber = selectedWeek.isoWeek();
        const year = selectedWeek.year();

        try {
            const response = await axios.get(tr('{{API_BASE_URL}}/users-schedules?week={{weekNumber}}&year={{year}}', {
                API_BASE_URL: config.API_BASE_URL,
                weekNumber,
                year
            }));
            const userSelections = {};
    
            response.data.forEach(({ schedule, user }) => {
                if (!userSelections[schedule]) {
                    userSelections[schedule] = {};
                }
                userSelections[schedule][user] = true; // Mark user as selected
            });
    
            setSelectedUsers(userSelections);
        } catch (error) {
            console.error("Error fetching users' schedules:", error);
        }
    };

    const fetchUsers = async () => {
        try {
            const response = await axios.get(tr('{{API_BASE_URL}}/users', {
                API_BASE_URL: config.API_BASE_URL
            }));
            setUsers(response.data);
        } catch (error) {
            console.error('Error fetching users:', error);
        }
    };

    const fetchCategories = async () => {
        try {
            const response = await axios.get(tr('{{API_BASE_URL}}/users/roles', {
                API_BASE_URL: config.API_BASE_URL
            }));
            const roles = response.data.map(role => ({
                id: role.id,
                name: role.name,
                role: role.role,
                area: null                         
            }));

            setCategories([...roles, ...areas.map(area => ({
                id: area.id,
                name: area.name,
                role: 'at_service',
                area: area.id
            }))]);
        } catch (error) {
            console.error('Error fetching categories:', error);
        }
    };

    const handleCheckboxChange = (userId, slotId) => {
        setSelectedUsers(prev => ({
            ...prev,
            [slotId]: {
                ...prev[slotId],
                [userId]: !prev[slotId]?.[userId] // Toggle selection
            }
        }));
    };

    const handleWeekChange = (increment) => {
        const newWeek = selectedWeek.clone().add(increment, 'weeks');
        //if (newWeek.isBefore(moment().startOf('isoWeek'))) return;   
        setSelectedWeek(newWeek);
    };

    const handleSaveSchedules = async () => {
        const weekNumber = selectedWeek.isoWeek();
        const year = selectedWeek.year();

        const formattedSchedules = Object.entries(selectedUsers).map(([scheduleId, users]) => ({
            scheduleId,
            userIds: Object.keys(users).filter(userId => users[userId]) // Get only selected users
        }));

        try {
            await axios.post(tr('{{API_BASE_URL}}/users-schedules/bulk', {
                API_BASE_URL: config.API_BASE_URL
            }), {
                weekStart: selectedWeek.format("YYYY-MM-DD"),
                weekEnd: selectedWeek.clone().add(6, "days").format("YYYY-MM-DD"),
                weekNumber,
                year,
                schedules: formattedSchedules
            });
    
            alert("Schedules saved successfully!");
            fetchUsersSchedules(); // Refresh data
        } catch (error) {
            console.error("Error saving users' schedules:", error);
        }
    };

    const handleReplicateSchedules = async () => {
        const direction = selectedWeek.isAfter(moment().startOf("isoWeek")) ? "future" : "past";
        const weekNumber = selectedWeek.isoWeek();
        const year = selectedWeek.year();

        try {
            const res = await axios.post(tr('{{API_BASE_URL}}/schedules/replicate', {
                API_BASE_URL: config.API_BASE_URL
            }), {
                selectedWeek: selectedWeek.format("YYYY-MM-DD"),
                direction,
                week: weekNumber,  
                year: year        
            });
    
            alert(res.data.message);
            fetchUsersSchedules(); // Refresh the data
        } catch (error) {
            console.error("Error replicating user schedules:", error);
            alert("Failed to replicate schedules.");
        }
    };

    const getUsersByRole = (role) => {
        return users.filter(user => user[role]?.data?.[0]);
    };

    const renderTableHeaders = () => {
        return (
            <tr>
                <th>{tr('Role / Area')}</th>
                {[...Array(7)].map((_, i) => (
                    <th key={i} style={{color: selectedWeek.clone().day(i + 1).format('D/M/Y') === moment().format('D/M/Y')? 'darkOrange': 'darkBlue'}}>{selectedWeek.clone().day(i + 1).format('dddd, D/M')}</th>
                ))}
            </tr>
        );
    };

    const renderTableRows = () => {
        return categories.map((category, rowIndex) => (
            <tr key={rowIndex}>
                <td><strong>{category.name}</strong></td>
                {[...Array(7)].map((_, day) => {
                    //const cellSlots = schedules.filter(slot => slot.area === category.area && slot.role === category.id && slot.day === day);
                    
                   const cellSlots = schedules.filter(slot =>
                     slot.day === day &&
                     (
                       (category.area ? // area row: match by area id only
                       slot.area === category.area : slot.area == null && slot.role === category.id) // role row: no area, match role id
                     )
                   );
                    
                    
                    
                    return (
                        <td key={day} style={{ verticalAlign: 'top', minWidth: '145px' }}>
                          <div style={{display: 'inline-grid', width: '100%', textAlign: 'center'}}>
                            {cellSlots.map((slot, index) => (
                                <div key={index} style={{display: 'inline-grid'}}>
                                    <b style={{color: selectedWeek.clone().day(day + 1).format('D/M/Y') === moment().format('D/M/Y')? 'darkOrange': 'darkBlue', background: selectedWeek.clone().day(day + 1).format('D/M/Y') === moment().format('D/M/Y')? '#fee' : '#eef', borderTop: '2px solid '+(selectedWeek.clone().day(day + 1).format('D/M/Y') === moment().format('D/M/Y')? 'darkOrange': 'darkBlue'), marginTop: '2px', textShadow: '1px 1px 1px '+(selectedWeek.clone().day(day + 1).format('D/M/Y') === moment().format('D/M/Y')? 'black': 'white')}}>{slot.starts.slice(0, -3)} - {slot.ends.slice(0, -3)}</b>
                                    <div style={{display: 'grid', textAlign: 'left'}}>
                                        {getUsersByRole(category.role).map(user => (
                                            <label key={user.id} style={{float: 'left', height: '18px', top: '-2px', position: 'relative'}}>
                                                <input type="checkbox" 
                                                        value={user.id} 
                                                        checked={!!selectedUsers[slot.id]?.[user.id]}
                                                        style={{height: '14px', position: 'relative', bottom: '-2px'}}
                                                        onChange={() => handleCheckboxChange(user.id, slot.id)} 
                                                />
                                                <span style={{height: '18px', fontWeight: selectedUsers[slot.id]?.[user.id] ? 'bold' : 'lighter'}}>
                                                  {user.name || '('+user.username+')'}
                                                </span>  
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            ))}
                          </div>
                        </td>
                    );
                })}
            </tr>
        ));
    };

    return (
        <div className={isTablet ? 'tablet-viewport' : ''} ref={viewportRef}>
            <div style={{display: 'inline-flex', margin: '25px 0px 15px 50px'}}>
              <h2 style={{marginTop: '-8px'}}>{tr('Weekly Scheduler')}</h2>
              <div>
                  <button style={{marginLeft: '20px'}} onClick={() => setShowModal(true)}>{tr('Slots Settings')}<b>{tr('⚙')}</b></button>
                  <button style={{marginLeft: '60px'}} onClick={() => handleWeekChange(-1)}>{tr('Previous Week')}<b>{tr('⏴')}</b></button>
                  <b title={'Week: '+selectedWeek.week()} style={{cursor: 'help', margin: '15px', color: 'darkBlue', textDecoration: selectedWeek+'' === moment().startOf('isoWeek')+''? 'underline' : 'none'}}>{selectedWeek.format('DD MMM')} - {selectedWeek.clone().add(6, 'days').format('DD MMM, YYYY')}</b>
                  <button onClick={() => handleWeekChange(1)}><b>{tr('⏵')}</b>{tr('Next Week')}</button>
                  {selectedWeek+'' !== moment().startOf('isoWeek')+'' && (
                    <button style={{marginLeft: '40px'}} onClick={()=>{setSelectedWeek(moment().startOf('isoWeek'))}}>{tr('Current Week')}</button>
                  )}
                  <button style={{marginLeft: (selectedWeek+'' === moment().startOf('isoWeek')+''?'180px':'40px')}} onClick={handleReplicateSchedules}>{tr('Replicate')}<b>{tr('⎘')}</b></button>
                  <button style={{marginLeft: '30px'}} onClick={handleSaveSchedules}>{tr('Save')}<b>{tr('💾')}</b></button>
              </div>            
            </div>
            <div className={isTablet ? 'fit-scale-wrap' : ''} style={isTablet ? { '--scale': scale, marginLeft: '20px' } : { marginLeft: '60px' }}>
              <div className={isTablet ? 'fit-scale' : ''} ref={contentRef}>        
                <table border="1">
                    <thead>
                        {renderTableHeaders()}
                    </thead>
                    <tbody>
                        {renderTableRows()}
                    </tbody>
                </table>
              </div>
            </div>
            {showModal && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <Schedules closeModal={() => setShowModal(false)} />
                    </div>
                </div>
            )}
        </div>
    );
};

export default Scheduler;
